import { useState } from "react";
import {
    CookieResult,
    MAX_COOKIE_JSON_STRING_LENGTH,
} from "@customTypes/cookies";
import { useGetCookieSyncConfig } from "./api/useGetCookieSyncConfig";
import { isEmptyList } from "@utils/commonUtils";
import { useAsyncEffect } from "use-async-effect";
import { GetCookieSynchronizationConfigurationResponse } from "@amzn/ermine-server-side-rendering-client";
import { getPortalConfig } from "@utils/configurationUtils";
import {
    getCookiesWithTimeout,
    isCookieSynchronizationExtensionInstalled,
} from "@utils/cookieSyncUtils";
import { MetricName } from "@constants/logging";
import { useGetLogger } from "./useGetLogger";

export const useGetCookie = () => {
    const getCookieSyncConfig = useGetCookieSyncConfig();
    const log = useGetLogger();

    const [isFetchingCookie, setIsFetchingCookie] = useState(false);
    const [cookieResult, setCookieResult] = useState<CookieResult>(null);

    const getCookie = async (): Promise<CookieResult> => {
        const portalConfig = getPortalConfig();
        const portalArn = portalConfig.portalArn;

        let cookieSyncConfig: GetCookieSynchronizationConfigurationResponse;
        try {
            cookieSyncConfig = await getCookieSyncConfig(portalArn);
        } catch {
            return {
                cookies: [],
                error: "CONFIG_FETCH_ERROR",
            };
        }

        if (
            isEmptyList(
                cookieSyncConfig.cookieSynchronizationConfiguration?.allowlist
            )
        ) {
            return {
                cookies: [],
                error: null,
            };
        }

        if (!isCookieSynchronizationExtensionInstalled()) {
            log.publishCounterMetric(MetricName.MISSING_COOKIE_SYNC_EXTENSION);
            return {
                cookies: [],
                error: "EXTENSION_NOT_INSTALLED",
            };
        }

        let result;
        try {
            result = await getCookiesWithTimeout(
                {
                    requestType: "getCookies",
                    allowListFilter:
                        cookieSyncConfig.cookieSynchronizationConfiguration
                            ?.allowlist,
                    blockListFilter:
                        cookieSyncConfig.cookieSynchronizationConfiguration
                            ?.blocklist,
                    customerDomain: portalConfig?.customerDomain,
                    portalArn: portalConfig?.portalArn,
                    userId: window.userSub,
                },
                15000 // Wait up to 15 seconds for response from extension
            );
        } catch (e) {
            log.logMessage(
                `Error occurred when communicating with extension: ${e}`
            );
            return {
                cookies: [],
                error: "EXTENSION_REQUEST_ERROR",
            };
        }

        if (result.type === "error") {
            log.logMessage(
                `Error occurred when communicating with extension: ${result.error}`
            );
            return {
                cookies: [],
                error: "EXTENSION_REQUEST_ERROR",
            };
        }

        // result.type === "cookies"
        const cookiesLength = JSON.stringify(result.cookies).length;
        if (cookiesLength > MAX_COOKIE_JSON_STRING_LENGTH) {
            return {
                cookies: [],
                error: "TOO_MANY_COOKIES",
            };
        } else {
            return {
                cookies: result.cookies,
                error: null,
            };
        }
    };

    useAsyncEffect(async () => {
        if (cookieResult != null) {
            // Fetch cookie only once
            return;
        }
        if (isFetchingCookie) {
            return;
        }
        setIsFetchingCookie(true);

        try {
            const result = await getCookie();
            setCookieResult(result);
        } catch {
            // Do nothing. The getCookie method will return an error code in the response.
        }

        setIsFetchingCookie(false);
    }, [isFetchingCookie, cookieResult]);

    return {
        cookieResult,
        setCookieResult,
        isFetchingCookie,
    };
};
